/**
 * The session module's imperative surface, and the only place the world's signals are routed into
 * the pure machine.
 *
 * Everything a caller outside this feature needs is here, and it is deliberately small:
 * `startAuthSession`, `awaitSessionResolved`, `signIn`, `signUpWithPassword`,
 * `resendSignUpConfirmation`, `signInWithProvider`, `requestPasswordReset`,
 * `resetPasswordWithCode`, `cancelPasswordReset`, `rehydrateSession`, `signOut`,
 * `consumeSessionEndReason`. The Supabase client, the transport wiring, the hydration call and the
 * single-flight guards are internals — nothing above this file knows they exist.
 *
 * **No decisions live here.** Every branch either asks `model/hydration.ts` what an outcome means or
 * hands an event to the reducer. That separation is the anti-forced-logout guarantee: there is no
 * code path in this file that can express "sign out because something failed", because there is no
 * such event to dispatch.
 */

import { AuthError as SupabaseAuthError } from '@supabase/supabase-js';

import { getAuthMe } from '~/shared/api/generated/endpoints/auth/auth';
import { configureApi } from '~/shared/api/mutator';
import { browserTimeZone } from '~/shared/lib/occurrenceZone';
import { queryClient } from '~/shared/lib/queryClient';

import { isExistingEmailSignUp } from '../model/existingEmailSignUp';
import { sessionEventForHydration, type HydrationPhase } from '../model/hydration';
import type { SessionEvent } from '../model/sessionMachine';
import { redirectFrom, type SessionRoute } from '../model/sessionRoutes';

import { dispatchSessionEvent, readSession } from './sessionStore';
import { getAccessToken, getSupabaseClient, refreshAccessToken } from '../api/supabaseClient';

/** Thrown when a flow needs Supabase and the project is not configured. */
class SupabaseNotConfiguredError extends Error {
  constructor() {
    super(
      'Supabase is not configured: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are both required.',
    );
    this.name = 'SupabaseNotConfiguredError';
  }
}

/**
 * Which session an answer belongs to.
 *
 * A hydration request can still be in flight when the trainer signs out, and its answer would then
 * describe an identity that no longer has a session here — dispatching it would put the app back
 * into `signedIn` or `refused` after a deliberate logout. Bumping this on sign-out makes a late
 * answer identifiable, and `hydrate` drops it.
 *
 * This lives in the controller rather than the reducer on purpose. Dropping an answer that belongs
 * to a dead session is not a decision about what a signal *means* — it is the same thing an aborted
 * request does. Encoding it as a reducer guard is what broke the primary path once already: during a
 * fresh sign-in the status is `signedOut` until hydration answers, so a "not while signed out" guard
 * dropped the answer it existed to deliver.
 */
let sessionEpochValue = 0;

/**
 * Which session is current, for code outside this slice that has to drop work belonging to a dead
 * one.
 *
 * Read SYNCHRONOUSLY, and that is the whole reason it exists as a number rather than as a store
 * subscription. `signOut` bumps it before it cancels anything, so a cancellation callback running
 * between `cancelQueries()` and `clear()` can already see that the session has ended — a React
 * subscription to `useSessionStatus` cannot, because the status only changes after `clear()` has
 * run and a re-render is a frame away. The plan library's prefetch queue re-seeded the cache in
 * exactly that window.
 *
 * The value is opaque: compare it with one taken earlier, never interpret it.
 */
export function sessionEpoch(): number {
  return sessionEpochValue;
}

/**
 * Work that should fly WITH `GET /auth/me` rather than after it — a read that needs only the bearer
 * token, which exists by the time hydration starts. The one listener today is the brand colour's
 * prefetch (`app/lib/brandPaint.ts`), so a first sign-in's shell does not wait a round trip longer
 * for its colour. A listener is called synchronously and must only START its work; a throw is
 * contained here, because nothing it does may delay or fail the session.
 */
const hydrationStartListeners = new Set<() => void>();

export function onSessionHydrationStart(listener: () => void): () => void {
  hydrationStartListeners.add(listener);
  return () => {
    hydrationStartListeners.delete(listener);
  };
}

function announceHydrationStart(): void {
  for (const listener of hydrationStartListeners) {
    try {
      listener();
    } catch (error) {
      console.warn('[auth] a hydration-start listener threw; the session is unaffected', error);
    }
  }
}

/**
 * Work that must happen when the trainer SIGNS OUT deliberately — `signOut`, from every screen that
 * offers it. The one listener today is the plan builder's crash recovery (`app/main.tsx`): an unsaved
 * plan in this tab's memory would otherwise be offered to the next trainer to sign in here, and its
 * device copy belongs to the trainer who is leaving.
 *
 * Called synchronously, BEFORE the session changes, so `readSession().user` still names the outgoing
 * trainer. Not called when a session merely expires: `onSessionEnd` (in `sessionStore.ts`) fires for
 * every end, and its listener takes the plan out of the tab while leaving the device copy for the
 * same trainer's return. A throw is contained here, like the
 * hydration listeners', because nothing a listener does may stop a sign-out.
 */
const signOutListeners = new Set<() => void>();

export function onSignOut(listener: () => void): () => void {
  signOutListeners.add(listener);
  return () => {
    signOutListeners.delete(listener);
  };
}

function announceSignOut(): void {
  for (const listener of signOutListeners) {
    try {
      listener();
    } catch (error) {
      console.warn('[auth] a sign-out listener threw; the sign-out continues', error);
    }
  }
}

/**
 * `GET /auth/me` — the single hydration call every session path converges on. Single-flight, so a
 * route guard, a recovery kick and a fresh sign-in racing each other make one request.
 *
 * Returns the event it produced plus the failure behind it, because `signIn` needs the original
 * error to map onto the form's surfaces and the event alone does not carry it.
 */
let hydrationInFlight: Promise<{ event: SessionEvent; error: unknown }> | null = null;

function hydrate(phase: HydrationPhase): Promise<{ event: SessionEvent; error: unknown }> {
  hydrationInFlight ??= (async () => {
    const epoch = sessionEpochValue;
    let event: SessionEvent;
    let error: unknown = undefined;

    announceHydrationStart();
    try {
      // No client-side timeout, on purpose: this is the request most likely to land on a cold
      // Supabase JWKS cache right after a backend deploy, which costs one extra round-trip and
      // succeeds (authentication ADR §8).
      // The browser's zone rides on the one read every client makes first: the backend stores it for
      // an account that has none of its own yet (the-day-is-classified-in-a-supplied-zone ADR) and
      // never overwrites one the person reported. Without it a trainer who only uses the web had no
      // zone, and booking refused every slots read with `trainer_zone_unknown`.
      const zone = browserTimeZone();
      const me = await getAuthMe(zone === null ? undefined : { timeZoneId: zone });
      event = sessionEventForHydration({ ok: true, me }, phase);
    } catch (caught: unknown) {
      error = caught;
      event = sessionEventForHydration({ ok: false, error: caught }, phase);
    }

    if (epoch !== sessionEpochValue) return { event, error };

    dispatchSessionEvent(event);

    if (event.type === 'ROLE_REFUSED') {
      // Authentication ADR §2: a rejected identity must not be left sitting in storage as a live
      // session. The reducer ignores the resulting SIGNED_OUT while `refused`, so the refusal
      // screen survives the sign-out that puts it there.
      await signOutOfSupabase();
    }

    return { event, error };
  })().finally(() => {
    hydrationInFlight = null;
  });

  return hydrationInFlight;
}

/** Drop the local Supabase session. Never throws — logout must not be blockable by connectivity. */
async function signOutOfSupabase(): Promise<void> {
  try {
    // `scope: 'local'` is an owner decision: signing out of the web app must not revoke the
    // trainer's session on their phone.
    await getSupabaseClient()?.auth.signOut({ scope: 'local' });
  } catch (error) {
    // supabase-js has already dropped the local session even when the network call failed.
    console.warn('[auth] signOut failed; continuing with the local sign-out', error);
  }
}

/**
 * Resolve the persisted session exactly once per page load.
 *
 * Two properties matter and both come from the reducer, not from here: a cold start with nothing in
 * storage is the only failure allowed to land on the sign-in screen, and a cold start that finds a
 * session it cannot confirm degrades instead.
 */
async function restore(): Promise<void> {
  const client = getSupabaseClient();
  if (!client) {
    dispatchSessionEvent({ type: 'BOOT_NO_SESSION' });
    return;
  }

  const hasSession = await client.auth
    .getSession()
    .then(({ data }) => data.session !== null)
    // A storage read that failed means "no session" — and specifically NOT "destroy the session".
    .catch(() => false);

  if (!hasSession) {
    dispatchSessionEvent({ type: 'BOOT_NO_SESSION' });
    return;
  }

  await hydrate('boot');
}

let resolution: Promise<void> | null = null;

/**
 * The session-resolution boundary (authentication ADR §3). Awaited by the root route's
 * `beforeLoad`, so no route decides anything — in either direction — before the persisted session
 * has settled. Idempotent and single-flight: every route awaits the same promise.
 */
export function awaitSessionResolved(): Promise<void> {
  resolution ??= restore();
  return resolution;
}

/**
 * Re-probe `/auth/me` to leave a degraded episode. A boot-degraded session (no user was ever
 * hydrated) has nothing to flip back to, so recovery has to come through hydration.
 */
function recoverIfDegraded(): void {
  const session = readSession();
  if (session.status !== 'degraded') return;

  if (session.user !== null) {
    dispatchSessionEvent({ type: 'SESSION_RECOVERED' });
    return;
  }

  void hydrate('live');
}

/**
 * Wire the session module into the app. Called once from `main.tsx`, **before the router is
 * created**, because `configureApi` has to be in place before any generated call can run.
 *
 * Returns a teardown for tests and hot-module replacement.
 */
export function startAuthSession(): () => void {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
  if (apiBaseUrl === '') {
    console.warn(
      '[api] VITE_API_BASE_URL is not set. Every request will be issued against this origin and ' +
        'answered by the dev server, which looks exactly like a backend outage. Copy .env.example ' +
        'to .env.local and restart Vite.',
    );
  }

  configureApi({
    getBaseUrl: () => apiBaseUrl,
    getAccessToken,
    // MUST be the single-flight seam — see `supabaseClient.refreshAccessToken`.
    refreshAccessToken,
    authEvents: {
      // The transport saw a 401 survive one refresh and one retry. It reports; it does not decide.
      onAuthTransientFailure: () => dispatchSessionEvent({ type: 'AUTH_TRANSIENT_FAILURE' }),
      // Any authenticated 2xx is proof the session works right now — the cheapest way out of a
      // degraded episode, and it costs nothing when the session is already healthy.
      onAuthenticatedSuccess: recoverIfDegraded,
    },
  });

  const disposers: (() => void)[] = [];

  const client = getSupabaseClient();
  if (client) {
    const { data } = client.auth.onAuthStateChange((event) => {
      switch (event) {
        case 'SIGNED_OUT':
          // supabase-js's own conclusion that the session is gone: a non-retryable refresh failure
          // against an expired token, or another tab signing out of the shared localStorage. The
          // only terminal signal this app does not originate itself.
          dispatchSessionEvent({ type: 'SUPABASE_SIGNED_OUT' });
          break;
        case 'TOKEN_REFRESHED':
          recoverIfDegraded();
          break;
        default:
          // INITIAL_SESSION / SIGNED_IN / USER_UPDATED / PASSWORD_RECOVERY / MFA: hydration is
          // explicit in this app, so none of them may move the session on their own.
          break;
      }
    });
    disposers.push(() => data.subscription.unsubscribe());
  }

  // Kick the boundary so the first route does not pay for starting it.
  void awaitSessionResolved();

  return () => {
    for (const dispose of disposers) dispose();
  };
}

/**
 * Sign in with an email and a password, then hydrate.
 *
 * Rejects with whatever failed — a supabase-js `AuthError` or our `ApiError` — and the screen turns
 * that into surfaces through `model/authErrors.ts`. Nothing is translated here.
 *
 * A Supabase sign-in that succeeds but cannot hydrate leaves us holding a session this app cannot
 * use, which is different from a session that went bad mid-use: it was never established, so
 * dropping it is not a forced logout. The reducer's guards make that safe — the trailing
 * `SIGNED_OUT` arrives while already signed out and is ignored, so no "session expired" banner
 * appears on the screen the trainer is still looking at.
 */
export async function signIn(credentials: {
  email: string;
  password: string;
}): Promise<void> {
  const client = getSupabaseClient();
  if (!client) throw new SupabaseNotConfiguredError();

  const { error } = await client.auth.signInWithPassword({
    email: credentials.email.trim(),
    password: credentials.password,
  });
  if (error) throw error;

  const { event, error: hydrationError } = await hydrate('live');

  if (event.type === 'AUTH_TRANSIENT_FAILURE' || event.type === 'BOOT_HYDRATION_INCONCLUSIVE') {
    await signOutOfSupabase();
    throw hydrationError;
  }
}

/**
 * Where Supabase sends the browser back to after a redirect it owns — the emailed confirmation link
 * and both OAuth providers.
 *
 * One URL for all three, and it is `/sign-in` rather than a dedicated callback route on purpose.
 * The session module already answers "where does this session belong" for every status, and that
 * route's guard asks it: a confirmed sign-up and a social first login both land here holding a live
 * session with no profile, and are forwarded to completion by the same rule that forwards them
 * anywhere else. A second callback route would be a second place that decision could be made.
 *
 * A consequence to carry to deployment, not a defect: this exact URL must be in the Supabase
 * project's Redirect URLs allow-list, or Supabase falls back to the project's Site URL and the
 * redirect lands on the marketing page — the "Dalej" path on the confirmation screen still
 * completes the sign-up, but OAuth cannot complete at all. Both hosted projects list their web
 * origin's `/sign-in`, and staging also lists `http://localhost:5173/sign-in` so a locally run
 * frontend can complete sign-in against it (`train-with-me-app/supabase/config.toml`).
 */
function authRedirectUrl(): string {
  const landing: SessionRoute = '/sign-in';
  return `${globalThis.location.origin}${landing}`;
}

/**
 * Create a Supabase account with an email and a password.
 *
 * **The account exists from the moment this resolves** — unconfirmed, but real. No backend call
 * happens here: the profile row is created later by `POST /auth/complete-profile`, which is why
 * `POST /auth/register-trainer` is not, and must not be, on this path (authentication ADR §2).
 *
 * Whether a confirmation step is needed is not this app's choice — it is the Supabase project's
 * "confirm email" setting, read off the response: a project with confirmations ON returns no
 * session and the caller shows the check-your-email screen; a project with them OFF returns one
 * immediately, and hydrating here sends the caller straight to profile completion.
 *
 * Rejects with the supabase-js `AuthError`; the screen maps it onto its surfaces.
 */
export async function signUpWithPassword(credentials: {
  email: string;
  password: string;
}): Promise<{ needsConfirmation: boolean }> {
  const client = getSupabaseClient();
  if (!client) throw new SupabaseNotConfiguredError();

  const { data, error } = await client.auth.signUp({
    email: credentials.email.trim(),
    password: credentials.password,
    options: { emailRedirectTo: authRedirectUrl() },
  });
  if (error) throw error;

  // Anti-enumeration fake success for a confirmed duplicate email — no email will ever arrive.
  // Rethrown as the error Supabase would have sent, so the screen anchors it on the email field.
  if (isExistingEmailSignUp(data)) {
    throw new SupabaseAuthError('An account with this email already exists.', 422, 'user_already_exists');
  }

  if (data.session === null) return { needsConfirmation: true };

  await hydrate('live');
  return { needsConfirmation: false };
}

/** Send the confirmation email again for an account that exists but is not confirmed. */
export async function resendSignUpConfirmation(email: string): Promise<void> {
  const client = getSupabaseClient();
  if (!client) throw new SupabaseNotConfiguredError();

  const { error } = await client.auth.resend({
    type: 'signup',
    email: email.trim(),
    options: { emailRedirectTo: authRedirectUrl() },
  });
  if (error) throw error;
}

/**
 * A recovery code that has been verified but whose password update has not yet succeeded.
 *
 * `verifyOtp` SPENDS the code: a second verify with the same digits is refused. The password update
 * that follows can still be refused on its own terms — too weak for the project's policy, or the
 * same as the old one — and that must not cost the trainer the code they are still holding. So the
 * verified attempt is remembered here, and a retry with the same email and code skips straight to
 * the update. Reproduced from the mobile app's `AuthContext.resetPassword`.
 */
let verifiedRecovery: { email: string; code: string } | null = null;

/**
 * Send the password-reset email. It carries a six-digit code and no link, by the shared template's
 * own design (`train-with-me-app/supabase/templates/recovery.html`): the mobile app resets with
 * `verifyOtp({ type: 'recovery' })` against typed digits, and this app does exactly the same, so
 * one mail serves both clients and no `redirectTo` is sent.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const client = getSupabaseClient();
  if (!client) throw new SupabaseNotConfiguredError();

  const { error } = await client.auth.resetPasswordForEmail(email.trim());
  if (error) throw error;
}

/**
 * Verify the emailed code, set the new password, and sign back out.
 *
 * `verifyOtp` with `type: 'recovery'` opens a real session — that is how `updateUser` is authorised.
 * It never reaches the session machine: `onAuthStateChange` ignores `SIGNED_IN` and
 * `PASSWORD_RECOVERY` (hydration is explicit in this app), so the screen stays where it is. The
 * sign-out at the end is what returns the trainer to a signed-out browser that then signs in with
 * the new password like any other; the trailing `SIGNED_OUT` arrives while already signed out and
 * the reducer ignores it. Between verify and sign-out that transient session is in storage, which is
 * why `cancelPasswordReset` exists for every way of leaving the screen without finishing.
 */
export async function resetPasswordWithCode(input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  const client = getSupabaseClient();
  if (!client) throw new SupabaseNotConfiguredError();

  const attempt = { email: input.email.trim(), code: input.code };
  const alreadyVerified =
    verifiedRecovery !== null &&
    verifiedRecovery.email === attempt.email &&
    verifiedRecovery.code === attempt.code;

  if (!alreadyVerified) {
    const { error } = await client.auth.verifyOtp({
      email: attempt.email,
      token: attempt.code,
      type: 'recovery',
    });
    if (error) throw error;
    verifiedRecovery = attempt;
  }

  const { error } = await client.auth.updateUser({ password: input.newPassword });
  if (error) throw error;

  verifiedRecovery = null;
  await signOutOfSupabase();
}

/**
 * Why a password change was refused, or `ok` when it was not.
 *
 * A small closed union rather than the raw `AuthError`, for the reason the barrel exists: the
 * account screen may not import supabase-js, and a caller holding an `AuthError` would be narrowing
 * a vendor's error codes in a component. The screen turns one of these into a sentence.
 *
 * `reauthenticationRequired` is the one that is a project SETTING rather than a mistake: a Supabase
 * project with "secure password change" on refuses `updateUser` without a fresh nonce. This app has
 * no current-password field to offer — the owner removed that copy — so the sentence says to use
 * „Nie pamiętam hasła" instead. `unknown` covers everything else, including a network failure.
 */
export type PasswordUpdateOutcome =
  | 'ok'
  | 'weakPassword'
  | 'samePassword'
  | 'reauthenticationRequired'
  | 'unknown';

/**
 * Change the signed-in trainer's password, WITHOUT signing them out.
 *
 * The deliberate difference from `resetPasswordWithCode` above, which signs out at the end: that
 * flow opens a transient recovery session that must not be left in storage, while this one runs
 * inside the trainer's own live session and there is nothing to drop. Supabase emits `USER_UPDATED`,
 * which `onAuthStateChange` ignores — hydration is explicit in this app — so the screen stays where
 * it is and the session is untouched.
 *
 * It reports rather than throws, because every outcome here is something the form renders in its
 * own banner and none of them is exceptional.
 */
export async function updatePassword(newPassword: string): Promise<PasswordUpdateOutcome> {
  const client = getSupabaseClient();
  if (!client) throw new SupabaseNotConfiguredError();

  const { error } = await client.auth.updateUser({ password: newPassword });
  if (error === null) return 'ok';

  // `code` is supabase-js's stable identifier; `status` is not, and the message is prose that
  // changes with the project's language. Read in that order.
  const code = error.code ?? '';
  if (code === 'weak_password') return 'weakPassword';
  if (code === 'same_password') return 'samePassword';
  if (code === 'reauthentication_needed' || code === 'reauthentication_not_valid') {
    return 'reauthenticationRequired';
  }
  return 'unknown';
}

/**
 * Drop the transient session a verified-but-unfinished reset left behind — the trainer went back to
 * change the address, asked for a fresh code, or left the screen. Nothing to drop is not an error.
 */
export async function cancelPasswordReset(): Promise<void> {
  if (verifiedRecovery === null) return;
  verifiedRecovery = null;
  await signOutOfSupabase();
}

/** The providers this app offers. Narrower than supabase-js's `Provider` on purpose. */
export type AuthProvider = 'google' | 'apple';

/**
 * Hand the browser to a social provider.
 *
 * This is a **redirect** flow and is genuinely different from the mobile app's native id-token
 * flow — there is nothing to port. On success the call never returns in the usual sense: the tab
 * navigates away, and the session is established on the way back by `detectSessionInUrl`, which
 * exchanges the PKCE code during client initialisation. `getSession()` awaits that initialisation,
 * so `awaitSessionResolved` on the next load already sees the exchanged session and hydration runs
 * exactly as it does for a password sign-in. A social first login has no local profile, so it lands
 * on `profile_missing` and routes into completion by the existing rule.
 *
 * It rejects only when the redirect never happens — a provider that is not enabled on the project,
 * or an unreachable Supabase.
 */
export async function signInWithProvider(provider: AuthProvider): Promise<void> {
  const client = getSupabaseClient();
  if (!client) throw new SupabaseNotConfiguredError();

  const { error } = await client.auth.signInWithOAuth({
    provider,
    options: { redirectTo: authRedirectUrl() },
  });
  if (error) throw error;
}

/**
 * Re-read `/auth/me` and let the reducer place the answer.
 *
 * The one caller today is profile completion: the row it just created is what turns `incomplete`
 * into `signedIn`, and only this module may say so. Like every hydration it also starts the listeners
 * registered with `onSessionHydrationStart`, so the brand colour is refetched here too; that is
 * intended, and a hydration inside the app keeps the cached colour while it does. It deliberately reports nothing — a caller that
 * received the outcome could branch on it, and the routing answer already lives in
 * `routeForSessionStatus`.
 */
export async function rehydrateSession(): Promise<void> {
  await hydrate('live');
}

/**
 * Sign out at the trainer's request.
 *
 * The dispatch order is load-bearing and cost real debugging time on mobile: `USER_LOGOUT` must
 * land **before** `signOut()` is awaited. Supabase emits `SIGNED_OUT` part-way through that await,
 * and if the machine were still live at that moment the reducer would stamp `endReason: 'expired'`
 * — so a deliberate logout would drop the trainer onto the sign-in screen under a "your session
 * expired" banner. Dispatching first makes Supabase's event the trailing one, which the reducer
 * ignores once already signed out.
 */
export async function signOut(): Promise<void> {
  // Cancel first, then clear: a request from the previous session that lands after `clear()` would
  // otherwise re-seed the cache with the outgoing trainer's data.
  // Any hydration still in flight describes the outgoing trainer; its answer must not land.
  sessionEpochValue += 1;
  announceSignOut();

  await queryClient.cancelQueries();
  queryClient.clear();

  dispatchSessionEvent({ type: 'USER_LOGOUT' });

  await signOutOfSupabase();
}

/** Mark the end-of-session reason as shown, so a later visit to sign-in does not repeat it. */
export function consumeSessionEndReason(): void {
  dispatchSessionEvent({ type: 'END_REASON_CONSUMED' });
}

/**
 * Where a route guard should send the trainer, or `null` to stay. Every auth route's `beforeLoad` is
 * this one call, so the answer is the same everywhere and a route file stays a wiring declaration
 * with no access to the session's internals.
 */
export function sessionRedirectFrom(here: SessionRoute): SessionRoute | null {
  return redirectFrom(here, readSession().status);
}
